import React, { useState, useEffect } from 'react';
import './Sidebar.css';
import logoDefault from '../assets/img/logo-data-new.png';

const Sidebar = ({
  onPageChange,
  currentPage,
  onSignOut,
  user,
  isOpen,
  toggleSidebar,
  availableReports = [],
  currentReportId,
  onReportChange,
  userOrganization,
  userRole,
  userFeatures = {},
  onAdminPanelOpen
}) => {
  
  // 📂 Estado para controlar qué reportes están expandidos
  const [expandedReports, setExpandedReports] = useState({});
  
  // 🆕 Estado para controlar si la imagen del logo falló
  const [logoError, setLogoError] = useState(false);

  // Expandir el reporte actual por defecto
  useEffect(() => {
    if (currentReportId && !expandedReports[currentReportId]) {
      setExpandedReports(prev => ({
        ...prev,
        [currentReportId]: true
      }));
    }
  }, [currentReportId]);

  // 🆕 Reset del error cuando cambia la organización
  useEffect(() => {
    setLogoError(false);
  }, [userOrganization?.urlImage]);

  // 🔽 Toggle para expandir/colapsar un reporte
  const toggleReportExpansion = (reportId) => {
    setExpandedReports(prev => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  // 📄 Manejar clic en una página
  const handlePageClick = (reportId, pageName) => {
    if (reportId !== currentReportId) {
      onReportChange(reportId);
    }
    onPageChange(pageName);
    if (window.innerWidth < 768) {
      toggleSidebar();
    }
  };

  // 🖼️ Determinar qué logo mostrar
  const logoToShow = logoDefault;
  const orgLogo = userOrganization?.urlImage;
  const shouldShowOrgLogo = orgLogo && !logoError;

  // 🆕 Handler para errores de carga de imagen
  const handleImageError = () => {
   
    setLogoError(true);
  };

  return (
    <>
      {/* Overlay para móvil */}
      {isOpen && window.innerWidth < 768 && (
        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
      )}

      {/* Sidebar */}
      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          {/* Logo principal de DataBunker */}
          <img 
            src={logoToShow} 
            alt="DataBunker Logo" 
            className="sidebar-logo"
          />
          
          <div
            className={`user-info ${userRole?.toLowerCase() === 'admin' ? 'clickable' : ''}`}
            onClick={() => {
              console.log('[Sidebar] Click en user-info. Role:', userRole, '| onAdminPanelOpen:', !!onAdminPanelOpen);
              if (userRole?.toLowerCase() === 'admin' && onAdminPanelOpen) {
                console.log('[Sidebar] Abriendo AdminPanel...');
                onAdminPanelOpen();
              }
            }}
            title={userRole?.toLowerCase() === 'admin' ? 'Abrir Panel de Administracion' : ''}
          >
            {/* Logo de la organización o icono de fallback */}
            {shouldShowOrgLogo ? (
              <img
                src={orgLogo}
                alt={userOrganization?.name || 'Organization'}
                className="user-org-logo"
                onError={handleImageError}
              />
            ) : (
              <span className="user-icon">👤</span>
            )}

            <div className="user-details">
              <span className="user-name">{user?.username || 'Usuario'}</span>

              {/* Mostrar organización si existe */}
              {userOrganization && (
                <span className="user-org">{userOrganization.displayName}</span>
              )}

              {/* Mostrar rol si existe */}
              {userRole && (
                <span className="user-role">
                  {userRole}
                  {userRole?.toLowerCase() === 'admin' && <span className="admin-indicator"> </span>}
                </span>
              )}
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <h3 className="nav-title">Reportes</h3>
            
            {availableReports.length === 0 ? (
              <div className="nav-loading">Cargando reportes...</div>
            ) : (
              userFeatures.canAccessMultipleReports === false ? (
                availableReports.slice(0, 1).map((report) => (
                  <div key={report.id} className="report-group single-report">
                    {report.menu.map((page) => (
                      <button
                        key={`${report.id}-${page.name}`}
                        className={`nav-item ${currentPage === page.name ? 'active' : ''}`}
                        onClick={() => handlePageClick(report.id, page.name)}
                        data-tooltip={page.name}
                      >
                        <span className="nav-icon">{page.icon}</span>
                        <span className="nav-text">{page.name}</span>
                      </button>
                    ))}
                  </div>
                ))
              ) : (
                availableReports.map((report) => (
                  <div key={report.id} className="report-group">
                    <button
                      className={`report-header ${currentReportId === report.id ? 'active-report' : ''}`}
                      onClick={() => {
                        if (userFeatures.canSwitchReports !== false) {
                          toggleReportExpansion(report.id);
                        }
                      }}
                      disabled={userFeatures.canSwitchReports === false}
                      data-tooltip={report.name}
                    >
                      <span className="report-header-left">
                        <span className="report-icon">{report.icon}</span>
                        <span className="report-name">{report.name}</span>
                      </span>
                      {userFeatures.canSwitchReports !== false && (
                        <span className={`chevron ${expandedReports[report.id] ? 'expanded' : ''}`}>
                          ▼
                        </span>
                      )}
                    </button>

                    <div className={`report-pages ${expandedReports[report.id] ? 'expanded' : ''}`}>
                      {report.menu.map((page) => (
                        <button
                          key={`${report.id}-${page.name}`}
                          className={`nav-item ${
                            currentReportId === report.id && currentPage === page.name
                              ? 'active'
                              : ''
                          }`}
                          onClick={() => handlePageClick(report.id, page.name)}
                          data-tooltip={page.name}
                        >
                          <span className="nav-icon">{page.icon}</span>
                          <span className="nav-text">{page.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )
            )}
          </div>
        </nav>

        <div className="sidebar-footer">
          <button className="signout-button" onClick={onSignOut}>
            <span className="nav-icon">🚪</span>
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;